import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import styles from './Aviary.module.css';

// ----------------------------------------------------------------------
// FUNÇÕES AUXILIARES DE MÁSCARA E FORMATAÇÃO
// ----------------------------------------------------------------------

// Helper para remover todos os caracteres não numéricos
const removeMask = (value = '') => String(value).replace(/\D/g, '');

// Formata CEP: 00000-000 ou 00.000-000
const formatCEP = (value = '') => {
    const clean = removeMask(value);
    if (clean.length > 5) {
        return `${clean.slice(0, 2)}.${clean.slice(2, 5)}-${clean.slice(5, 8)}`;
    }
    if (clean.length > 2) {
        return `${clean.slice(0, 2)}.${clean.slice(2)}`;
    }
    return clean;
};

// Formata Telefone: (00) 0000-0000 ou (00) 0.0000-0000
const formatPhone = (value = '') => {
    const clean = removeMask(value);
    if (clean.length > 10) {
        return `(${clean.slice(0, 2)}) ${clean.slice(2, 3)}.${clean.slice(3, 7)}-${clean.slice(7, 11)}`;
    }
    if (clean.length > 6) {
        return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6, 10)}`;
    }
    if (clean.length > 2) {
        return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;
    }
    return clean;
};

// Formata CPF (000.000.000-00) ou CNPJ (00.000.000/0001-00) dinamicamente
const formatDocument = (value = '') => {
    const clean = removeMask(value);

    // CNPJ (mais de 11 dígitos)
    if (clean.length > 11) {
        return clean
            .slice(0, 14)
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/\.(\d{3})(\d)/, '.$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2');
    }

    // CPF
    return clean
        .slice(0, 11)
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

function Aviary() {
    const [aviarys, setAviarys] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAviary, setSelectedAviary] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Controle de Edição nas telas de detalhes
    const [isEditingAviary, setIsEditingAviary] = useState(false);
    const [isEditingUser, setIsEditingUser] = useState(false);

    // Controle do Modal de Troca de Senha
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        password: '',
        newpassword: '',
        confirmpassword: ''
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // Visibilidade dos campos de senha
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Estado dos Formulários
    const [formData, setFormData] = useState({
        name: '', email: '', document: '', phone: '',
        cep: '', street: '', number: '', complement: '',
        neighborhood: '', city: '', state: ''
    });
    const [userFormData, setUserFormData] = useState({
        name: '', email: '', document: '', phone: ''
    });
    const [image, setImage] = useState(null);

    // Estados e Ref adicionais para manipulação de imagem do Criatório
    const [aviaryImageFile, setAviaryImageFile] = useState(null);
    const [aviaryImagePreview, setAviaryImagePreview] = useState(null);
    const aviaryInputFileRef = useRef(null);

    // Estados e Ref adicionais para manipulação de imagem do Usuário
    const [userImageFile, setUserImageFile] = useState(null);
    const [userImagePreview, setUserImagePreview] = useState(null);
    const userInputFileRef = useRef(null);

    // URL base do backend para imagens
    const BASE_IMAGE_URL = process.env.REACT_APP_API_URL || 'https://reprobird.com.br/api';

    // Busca os dados do usuário logado e da lista de criatórios
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const authHeader = { headers: { Authorization: `Bearer ${JSON.parse(token)}` } };

            // 1. Busca usuário atual
            try {
                const userRes = await api.get('/user/checkuser', authHeader);
                setCurrentUser(userRes.data);
            } catch (err) {
                console.error('Erro ao buscar usuário logado:', err);
            }

            // 2. Busca criatórios do usuário
            const response = await api.get('/aviary/myaviarys', authHeader);
            const data = response.data.aviary || response.data.aviaries || response.data.aviarys || [];
            setAviarys(data);

            setSelectedAviary((prev) => {
                if (prev) {
                    const updated = data.find(a => a._id === prev._id);
                    return updated || prev;
                }
                return null;
            });

            setSelectedUser((prevUser) => {
                if (prevUser) {
                    for (const item of data) {
                        const owner = typeof item.user === 'object' ? item.user : null;
                        if (owner && owner._id === prevUser._id) {
                            return owner;
                        }
                    }
                }
                return prevUser;
            });

        } catch (error) {
            console.error('Erro ao buscar criatórios:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    function handleStartEditAviary() {
        if (!selectedAviary) return;
        const address = selectedAviary.address || {};
        setFormData({
            name: selectedAviary.name || '',
            email: selectedAviary.email || '',
            document: formatDocument(selectedAviary.document || ''),
            phone: formatPhone(selectedAviary.phone || ''),
            cep: formatCEP(address.cep || selectedAviary.cep || ''),
            street: address.street || selectedAviary.street || '',
            number: address.number || selectedAviary.number || '',
            complement: address.complement || selectedAviary.complement || '',
            neighborhood: address.neighborhood || selectedAviary.neighborhood || '',
            city: address.city || selectedAviary.city || '',
            state: address.state || selectedAviary.state || ''
        });
        setAviaryImageFile(null);
        setAviaryImagePreview(selectedAviary.image ? `${BASE_IMAGE_URL}/images/aviary/${selectedAviary.image}` : null);
        setIsEditingAviary(true);
    }

    function handleStartEditUser() {
        if (!selectedUser) return;
        setUserFormData({
            name: selectedUser.name || '',
            email: selectedUser.email || '',
            document: formatDocument(selectedUser.document || ''),
            phone: formatPhone(selectedUser.phone || '')
        });
        setUserImageFile(null);
        setUserImagePreview(selectedUser.image ? `${BASE_IMAGE_URL}/images/user/${selectedUser.image}` : null);
        setIsEditingUser(true);
    }

    // Manipula inputs do criatório com formatação em tempo real
    function handleChange(e) {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === 'phone') formattedValue = formatPhone(value);
        if (name === 'document') formattedValue = formatDocument(value);
        if (name === 'cep') formattedValue = formatCEP(value);

        setFormData({ ...formData, [name]: formattedValue });
    }

    // Manipula inputs do usuário com formatação em tempo real
    function handleUserChange(e) {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === 'phone') formattedValue = formatPhone(value);
        if (name === 'document') formattedValue = formatDocument(value);

        setUserFormData({ ...userFormData, [name]: formattedValue });
    }

    // Manipula inputs do formulário de troca de senha
    function handlePasswordChange(e) {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    }

    function handleFileChange(e) {
        setImage(e.target.files[0]);
    }

    function handleAviaryImageChange(e) {
        const file = e.target.files[0];
        if (file) {
            setAviaryImageFile(file);
            setAviaryImagePreview(URL.createObjectURL(file));
        }
    }

    function handleUserImageChange(e) {
        const file = e.target.files[0];
        if (file) {
            setUserImageFile(file);
            setUserImagePreview(URL.createObjectURL(file));
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setErrorMessage('');

        const data = new FormData();
        Object.keys(formData).forEach((key) => {
            let val = formData[key];
            if (['document', 'phone', 'cep'].includes(key)) {
                val = removeMask(val);
            }
            data.append(key, val);
        });
        if (image) data.append('image', image);

        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/aviary/create', data, {
                headers: {
                    Authorization: `Bearer ${JSON.parse(token)}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert(response.data.message || 'Criatório cadastrado com sucesso!');
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            setErrorMessage(error.response?.data?.message || 'Erro ao cadastrar criatório.');
        }
    }

    async function handleUpdateAviary(e) {
        e.preventDefault();
        setErrorMessage('');

        const data = new FormData();
        Object.keys(formData).forEach((key) => {
            let val = formData[key];
            if (['document', 'phone', 'cep'].includes(key)) {
                val = removeMask(val);
            }
            data.append(key, val);
        });
        if (aviaryImageFile) {
            data.append('image', aviaryImageFile);
        } else if (image) {
            data.append('image', image);
        }

        try {
            const token = localStorage.getItem('token');
            const response = await api.patch(`/aviary/edit/${selectedAviary._id}`, data, {
                headers: {
                    Authorization: `Bearer ${JSON.parse(token)}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert(response.data.message || 'Criatório atualizado com sucesso!');
            setIsEditingAviary(false);
            fetchData();
        } catch (error) {
            setErrorMessage(error.response?.data?.message || 'Erro ao atualizar criatório.');
        }
    }

    async function handleUpdateUser(e) {
        e.preventDefault();
        setErrorMessage('');

        const data = new FormData();
        data.append('name', userFormData.name);
        data.append('email', userFormData.email);
        data.append('document', removeMask(userFormData.document));
        data.append('phone', removeMask(userFormData.phone));

        if (userImageFile) {
            data.append('image', userImageFile);
        }

        try {
            const token = localStorage.getItem('token');
            const response = await api.patch(`/user/edit/${selectedUser._id}`, data, {
                headers: { 
                    Authorization: `Bearer ${JSON.parse(token)}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert(response.data.message || 'Usuário atualizado com sucesso!');
            setIsEditingUser(false);
            fetchData();
        } catch (error) {
            setErrorMessage(error.response?.data?.message || 'Erro ao atualizar usuário.');
        }
    }

    // Submete a alteração de senha
    async function handleResetPassword(e) {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (passwordData.newpassword !== passwordData.confirmpassword) {
            setPasswordError('A nova senha e a confirmação de senha precisam ser iguais!');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await api.patch(`/user/reset-password/${selectedUser._id}`, passwordData, {
                headers: {
                    Authorization: `Bearer ${JSON.parse(token)}`
                }
            });

            setPasswordSuccess(response.data.message || 'Senha atualizada com sucesso!');
            setPasswordData({ password: '', newpassword: '', confirmpassword: '' });

            setTimeout(() => {
                setIsChangingPassword(false);
                setPasswordSuccess('');
            }, 2000);

        } catch (error) {
            setPasswordError(error.response?.data?.message || 'Erro ao redefinir a senha.');
        }
    }

    const filteredAviarys = aviarys.filter((item) => {
        const term = searchTerm.toLowerCase();
        const name = (item.name || '').toLowerCase();
        const city = (item.address?.city || item.city || '').toLowerCase();
        const document = (item.document || '').toLowerCase();
        return name.includes(term) || city.includes(term) || document.includes(term);
    });

    if (loading) return <p className={styles.loading}>Carregando dados...</p>;

    // 🏛️ TELA DE DETALHES DO CRIATÓRIO
    if (selectedAviary) {
        const address = selectedAviary.address || {};
        const fullAddress = [
            address.street || selectedAviary.street,
            address.number || selectedAviary.number,
            address.neighborhood || selectedAviary.neighborhood,
            address.city || selectedAviary.city,
            address.state || selectedAviary.state
        ].filter(Boolean).join(', ');

        const aviaryAvatarSrc = isEditingAviary 
            ? aviaryImagePreview 
            : (selectedAviary.image ? `${BASE_IMAGE_URL}/images/aviary/${selectedAviary.image}` : null);

        return (
            <div className={styles.container}>
                <button className={styles.back_button} onClick={() => { setSelectedAviary(null); setIsEditingAviary(false); }}>
                    ← Voltar para criatórios
                </button>

                <div className={styles.aviary_detail_header}>
                    <div 
                        className={styles.aviary_icon_box_large}
                        style={{ 
                            cursor: isEditingAviary ? 'pointer' : 'default', 
                            overflow: 'hidden', 
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={() => { if (isEditingAviary && aviaryInputFileRef.current) aviaryInputFileRef.current.click(); }}
                        title={isEditingAviary ? "Clique para alterar a imagem" : ""}
                    >
                        {aviaryAvatarSrc ? (
                            <img 
                                src={aviaryAvatarSrc} 
                                alt={selectedAviary.name} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            '🏛️'
                        )}

                        {isEditingAviary && (
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                                color: '#fff',
                                fontSize: '10px',
                                textAlign: 'center',
                                padding: '3px 0'
                            }}>
                                📷 Alterar
                            </div>
                        )}
                    </div>
                    <div className={styles.aviary_header_info}>
                        <h2>{selectedAviary.name}</h2>
                        <p>{address.city || selectedAviary.city || 'Cidade não informada'} - {address.state || selectedAviary.state || ''}</p>
                    </div>
                    {!isEditingAviary && (
                        <button className={styles.btn_action} style={{ marginLeft: 'auto' }} onClick={handleStartEditAviary}>
                            ✏️ Editar Criatório
                        </button>
                    )}
                </div>

                {isEditingAviary ? (
                    <div className={styles.section_box}>
                        <h3>Editar Criatório</h3>
                        {errorMessage && <p className={styles.error_msg}>{errorMessage}</p>}
                        
                        <input 
                            type="file" 
                            ref={aviaryInputFileRef} 
                            style={{ display: 'none' }} 
                            accept="image/*" 
                            onChange={handleAviaryImageChange} 
                        />

                        <form onSubmit={handleUpdateAviary} className={styles.form}>
                            <label>Nome</label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                            <label>E-mail</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                            <label>Documento (CPF/CNPJ)</label>
                            <input type="text" name="document" value={formData.document} onChange={handleChange} required maxLength={18} />
                            <label>Telefone</label>
                            <input type="text" name="phone" value={formData.phone} onChange={handleChange} required maxLength={16} />
                            <label>CEP</label>
                            <input type="text" name="cep" value={formData.cep} onChange={handleChange} required maxLength={10} />
                            <label>Rua</label>
                            <input type="text" name="street" value={formData.street} onChange={handleChange} required />
                            <label>Número</label>
                            <input type="text" name="number" value={formData.number} onChange={handleChange} />
                            <label>Complemento</label>
                            <input type="text" name="complement" value={formData.complement} onChange={handleChange} />
                            <label>Bairro</label>
                            <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleChange} required />
                            <label>Cidade</label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} required />
                            <label>Estado (UF)</label>
                            <input type="text" name="state" value={formData.state} onChange={handleChange} required />
                            
                            <div className={styles.modal_actions} style={{ marginTop: '15px' }}>
                                <button type="submit" className={styles.btn_submit}>Salvar</button>
                                <button type="button" className={styles.btn_cancel} onClick={() => setIsEditingAviary(false)}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <>
                        <div className={styles.section_box}>
                            <h3>📋 Dados Cadastrais</h3>
                            <div className={styles.info_grid}>
                                <div className={styles.info_card}><span>E-MAIL</span><strong>{selectedAviary.email || '-'}</strong></div>
                                <div className={styles.info_card}><span>TELEFONE</span><strong>{selectedAviary.phone ? formatPhone(selectedAviary.phone) : '-'}</strong></div>
                                <div className={styles.info_card}><span>DOCUMENTO (CPF/CNPJ)</span><strong>{selectedAviary.document ? formatDocument(selectedAviary.document) : '-'}</strong></div>
                                <div className={styles.info_card}><span>CEP</span><strong>{address.cep || selectedAviary.cep ? formatCEP(address.cep || selectedAviary.cep) : '-'}</strong></div>
                            </div>
                        </div>

                        <div className={styles.section_box}>
                            <h3>📍 Endereço Completo</h3>
                            <div className={styles.address_card}>
                                <p>{fullAddress || 'Endereço não cadastrado.'}</p>
                                {(address.complement || selectedAviary.complement) && (
                                    <span>Complemento: {address.complement || selectedAviary.complement}</span>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // 👤 TELA DE DETALHES DO USUÁRIO
    if (selectedUser) {
        const userAvatarSrc = isEditingUser 
            ? userImagePreview 
            : (selectedUser.image ? `${BASE_IMAGE_URL}/images/user/${selectedUser.image}` : null);

        return (
            <div className={styles.container}>
                <button className={styles.back_button} onClick={() => { setSelectedUser(null); setIsEditingUser(false); }}>
                    ← Voltar para criatórios
                </button>

                <div className={styles.aviary_detail_header}>
                    <div 
                        className={styles.aviary_icon_box_large} 
                        style={{ 
                            cursor: isEditingUser ? 'pointer' : 'default', 
                            overflow: 'hidden', 
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={() => { if (isEditingUser && userInputFileRef.current) userInputFileRef.current.click(); }}
                        title={isEditingUser ? "Clique para alterar a foto" : ""}
                    >
                        {userAvatarSrc ? (
                            <img 
                                src={userAvatarSrc} 
                                alt={selectedUser.name} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            '👤'
                        )}

                        {isEditingUser && (
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                                color: '#fff',
                                fontSize: '10px',
                                textAlign: 'center',
                                padding: '3px 0'
                            }}>
                                📷 Alterar
                            </div>
                        )}
                    </div>

                    <div className={styles.aviary_header_info}>
                        <h2>{selectedUser.name}</h2>
                        <p>Dados do Usuário / Proprietário</p>
                    </div>
                    {!isEditingUser && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                            <button className={styles.btn_action} onClick={() => setIsChangingPassword(true)}>
                                🔒 Alterar Senha
                            </button>
                            <button className={styles.btn_action} onClick={handleStartEditUser}>
                                ✏️️ Editar Usuário
                            </button>
                        </div>
                    )}
                </div>

                {isEditingUser ? (
                    <div className={styles.section_box}>
                        <h3>Editar Usuário</h3>
                        {errorMessage && <p className={styles.error_msg}>{errorMessage}</p>}
                        
                        <input 
                            type="file" 
                            ref={userInputFileRef} 
                            style={{ display: 'none' }} 
                            accept="image/*" 
                            onChange={handleUserImageChange} 
                        />

                        <form onSubmit={handleUpdateUser} className={styles.form}>
                            <label>Nome</label>
                            <input type="text" name="name" value={userFormData.name} onChange={handleUserChange} required />
                            <label>E-mail</label>
                            <input type="email" name="email" value={userFormData.email} onChange={handleUserChange} required />
                            <label>Documento (CPF/CNPJ)</label>
                            <input type="text" name="document" value={userFormData.document} onChange={handleUserChange} required maxLength={18} />
                            <label>Telefone</label>
                            <input type="text" name="phone" value={userFormData.phone} onChange={handleUserChange} required maxLength={16} />
                            
                            <div className={styles.modal_actions} style={{ marginTop: '15px' }}>
                                <button type="submit" className={styles.btn_submit}>Salvar</button>
                                <button type="button" className={styles.btn_cancel} onClick={() => setIsEditingUser(false)}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className={styles.section_box}>
                        <h3>📋 Dados Cadastrais</h3>
                        <div className={styles.info_grid}>
                            <br/>
                            <div className={styles.info_card}><span>NOME</span><strong>{selectedUser.name || '-'}</strong></div>
                            <div className={styles.info_card}><span>E-MAIL</span><strong>{selectedUser.email || '-'}</strong></div>
                            <div className={styles.info_card}><span>TELEFONE</span><strong>{selectedUser.phone ? formatPhone(selectedUser.phone) : '-'}</strong></div>
                            <div className={styles.info_card}><span>DOCUMENTO</span><strong>{selectedUser.document ? formatDocument(selectedUser.document) : '-'}</strong></div>
                            <div className={styles.info_card}><span>PLANO</span><strong>{selectedUser.plan ? selectedUser.plan.toUpperCase() : 'N/A'}</strong></div>
                            <div className={styles.info_card}>
                                <span>VALIDADE DO PLANO</span>
                                <strong>{selectedUser.expires_at 
                                    ? new Date(selectedUser.expires_at).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) 
                                    : 'N/A'}
                                </strong>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL DE ALTERAÇÃO DE SENHA */}
                {isChangingPassword && (
                    <div className={styles.modal_overlay}>
                        <div className={styles.modal_content}>
                            <h3>🔒 Alterar Senha</h3>
                            
                            {passwordError && <p className={styles.error_msg}>{passwordError}</p>}
                            {passwordSuccess && <p className={styles.success_msg} style={{ color: 'green', marginBottom: '10px' }}>{passwordSuccess}</p>}

                            <form onSubmit={handleResetPassword} className={styles.form}>
                                
                                {/* Campo Senha Atual */}
                                <label>Senha Atual *</label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        name="password" 
                                        value={passwordData.password} 
                                        onChange={handlePasswordChange} 
                                        required 
                                        placeholder="Digite a senha atual"
                                        style={{ width: '100%', paddingRight: '40px' }}
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#666',
                                            padding: 0
                                        }}
                                        title={showPassword ? "Ocultar senha" : "Exibir senha"}
                                    >
                                        {showPassword ? (
                                            /* Ícone Olho Fechado / Slashed Eye */
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                                <path d="M6.61 6.61A13.52 13.52 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                                <line x1="2" y1="2" x2="22" y2="22"></line>
                                            </svg>
                                        ) : (
                                            /* Ícone Olho Aberto / Eye */
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                {/* Campo Nova Senha */}
                                <label>Nova Senha *</label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input 
                                        type={showNewPassword ? "text" : "password"} 
                                        name="newpassword" 
                                        value={passwordData.newpassword} 
                                        onChange={handlePasswordChange} 
                                        required 
                                        placeholder="Digite a nova senha"
                                        style={{ width: '100%', paddingRight: '40px' }}
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#666',
                                            padding: 0
                                        }}
                                        title={showNewPassword ? "Ocultar senha" : "Exibir senha"}
                                    >
                                        {showNewPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                                <path d="M6.61 6.61A13.52 13.52 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                                <line x1="2" y1="2" x2="22" y2="22"></line>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                {/* Campo Confirmar Nova Senha */}
                                <label>Confirmar Nova Senha *</label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input 
                                        type={showConfirmPassword ? "text" : "password"} 
                                        name="confirmpassword" 
                                        value={passwordData.confirmpassword} 
                                        onChange={handlePasswordChange} 
                                        required 
                                        placeholder="Confirme a nova senha"
                                        style={{ width: '100%', paddingRight: '40px' }}
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#666',
                                            padding: 0
                                        }}
                                        title={showConfirmPassword ? "Ocultar senha" : "Exibir senha"}
                                    >
                                        {showConfirmPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                                <path d="M6.61 6.61A13.52 13.52 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                                <line x1="2" y1="2" x2="22" y2="22"></line>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                <div className={styles.modal_actions} style={{ marginTop: '15px' }}>
                                    <button type="submit" className={styles.btn_submit}>Atualizar Senha</button>
                                    <button 
                                        type="button" 
                                        className={styles.btn_cancel} 
                                        onClick={() => {
                                            setIsChangingPassword(false);
                                            setPasswordError('');
                                            setPasswordSuccess('');
                                            setPasswordData({ password: '', newpassword: '', confirmpassword: '' });
                                            setShowPassword(false);
                                            setShowNewPassword(false);
                                            setShowConfirmPassword(false);
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // 📋 TELA INICIAL (LISTAGEM)
    return (
        <div className={styles.container}>
            <div className={styles.top_header}>
                <div>
                    <h2><strong>Meus Dados</strong></h2>   
                    <p className={styles.subtitle}>Gerencie os dados do seu perfil e dos seus criatórios.</p>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {/* O botão só é renderizado caso o utilizador NÃO tenha criatórios */}
                    {aviarys.length === 0 && (
                        <button 
                            className={styles.btn_action} 
                            onClick={() => {
                                setFormData({
                                    name: '', email: '', document: '', phone: '',
                                    cep: '', street: '', number: '', complement: '',
                                    neighborhood: '', city: '', state: ''
                                });
                                setIsModalOpen(true);
                            }}
                        >
                            + Novo Criatório
                        </button>
                    )}
                </div>
            </div>

            {/* CARD FIXO DO USUÁRIO LOGADO (Independe de ter criatório cadastrado) */}
            {currentUser && (
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ marginBottom: '10px', fontSize: '1rem', color: '#666' }}>Meu Perfil</h3>
                    <div 
                        className={styles.aviary_row_card} 
                        onClick={() => setSelectedUser(currentUser)}
                    >
                        <div className={styles.aviary_icon_box} style={{ overflow: 'hidden' }}>
                            {currentUser.image ? (
                                <img 
                                    src={`${BASE_IMAGE_URL}/images/user/${currentUser.image}`} 
                                    alt={currentUser.name} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                '👤'
                            )}
                        </div>
                        <div className={styles.aviary_info}>
                            <h3>{currentUser.name}</h3>
                            <p>E-mail: {currentUser.email}</p>
                            <p>Plano: {currentUser.plan ? currentUser.plan.toUpperCase() : 'N/A'}</p>
                            <p>Validade: {currentUser.expires_at 
                                    ? new Date(currentUser.expires_at).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) 
                                    : 'N/A'}</p>
                        </div>
                        <div className={styles.arrow_icon}>→</div>
                    </div>
                </div>
            )}

            {/* LISTAGEM DOS CRIATÓRIOS */}
            <div className={styles.aviary_list}>
                <h3 style={{ marginBottom: '10px', fontSize: '1rem', color: '#666' }}>Meu Criatório</h3>
                {filteredAviarys.length > 0 ? (
                    filteredAviarys.map((item) => {
                        const city = item.address?.city || item.city || 'Cidade n/a';
                        const state = item.address?.state || item.state || '';

                        return (
                            <div key={item._id} style={{ marginBottom: '12px' }}>
                                <div 
                                    className={styles.aviary_row_card} 
                                    onClick={() => setSelectedAviary(item)}
                                >
                                    <div className={styles.aviary_icon_box} style={{ overflow: 'hidden' }}>
                                        {item.image ? (
                                            <img 
                                                src={`${BASE_IMAGE_URL}/images/aviary/${item.image}`} 
                                                alt={item.name} 
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            '🏛️'
                                        )}
                                    </div>
                                    <div className={styles.aviary_info}>
                                        <h3>{item.name}</h3>
                                        <p>{city} / {state}</p> 
                                        <p>{item.phone ? formatPhone(item.phone) : 'Sem telefone'}</p>
                                    </div>
                                    <div className={styles.arrow_icon}>→</div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className={styles.empty_text}>Nenhum criatório cadastrado no momento.</p>
                )}
            </div>

            {/* Modal de Cadastro de Criatório */}
            {isModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content}>
                        <h3>Cadastrar Criatório</h3>
                        {errorMessage && <p className={styles.error_msg}>{errorMessage}</p>}

                        <form onSubmit={handleSubmit} className={styles.form}>
                            <label>Nome do Criatório *</label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                            
                            <label>E-mail *</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                            
                            <label>Documento (CPF/CNPJ) *</label>
                            <input type="text" name="document" value={formData.document} onChange={handleChange} required maxLength={18} placeholder="CPF ou CNPJ" />
                            
                            <label>Telefone *</label>
                            <input type="text" name="phone" value={formData.phone} onChange={handleChange} required maxLength={16} placeholder="(00) 0.0000-0000" />
                            
                            <label>CEP *</label>
                            <input type="text" name="cep" value={formData.cep} onChange={handleChange} required maxLength={10} placeholder="00.000-000" />
                            
                            <label>Rua *</label>
                            <input type="text" name="street" value={formData.street} onChange={handleChange} required />
                            
                            <label>Número</label>
                            <input type="text" name="number" value={formData.number} onChange={handleChange} />
                            
                            <label>Complemento</label>
                            <input type="text" name="complement" value={formData.complement} onChange={handleChange} />
                            
                            <label>Bairro *</label>
                            <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleChange} required />
                            
                            <label>Cidade *</label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} required />
                            
                            <label>Estado (UF) *</label>
                            <input type="text" name="state" value={formData.state} onChange={handleChange} required />

                            <label>Imagem da Logo / Criatório</label>
                            <input type="file" name="image" accept="image/*" onChange={handleFileChange} />

                            <div className={styles.modal_actions}>
                                <button type="submit" className={styles.btn_submit}>Cadastrar</button>
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)} 
                                    className={styles.btn_cancel}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Aviary;